/**
 * Tune4Tune Terms of Service.
 *
 * NOT LEGAL ADVICE. Reviewed by a US attorney before launch.
 * Kept short and readable; the important protections (limitation of liability,
 * arbitration, refund flow) are in plain English so buyers actually understand
 * them before clicking Order.
 */
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const CONTACT_EMAIL = 'hallandpull@gmail.com';
const LEGAL_ENTITY = 'Haul And Pull LLC';
const JURISDICTION = 'the State of Florida, USA';
const LAST_UPDATED = 'February 2026';

function Section({ id, title, children }) {
  return (
    <section id={id} className="space-y-3 text-[#D4D4D8] text-sm leading-relaxed">
      <h2 className="text-xl font-heading font-bold text-white pt-6 border-t border-[#1a1a1a] first:border-none first:pt-0">
        {title}
      </h2>
      {children}
    </section>
  );
}

function List({ children }) {
  return <ul className="list-disc pl-6 space-y-1.5">{children}</ul>;
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-24 space-y-10" data-testid="terms-page">
        <header className="space-y-2">
          <p className="text-lime text-xs uppercase tracking-widest font-semibold">Legal</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Terms of Service</h1>
          <p className="text-[#A1A1AA] text-sm">Last updated: {LAST_UPDATED}</p>
        </header>

        <Section id="acceptance" title="1. Acceptance">
          <p>
            These Terms of Service ("Terms") form a binding agreement between you and{' '}
            <strong className="text-white">{LEGAL_ENTITY}</strong> ("Tune4Tune", "we", "us"). By registering, browsing, or ordering on the site you agree to these Terms and to our{' '}
            <Link to="/privacy" className="text-lime hover:underline">Privacy Policy</Link>.
          </p>
        </Section>

        <Section id="account" title="2. Your account">
          <List>
            <li>You must be at least 13 (16 in the EU / UK) to register.</li>
            <li>You are responsible for keeping your password secret. Enable two-factor authentication if it's available for your role.</li>
            <li>One person per account. Sharing accounts is not allowed.</li>
            <li>Notify us immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a> if you suspect unauthorized access.</li>
          </List>
        </Section>

        <Section id="how-it-works" title="3. How Tune4Tune works">
          <p>Tune4Tune is a marketplace. Fans place orders for custom dubplates and voiceovers; artists deliver them. We collect payment on behalf of the artist and release funds after the buyer accepts (or after the review window closes).</p>
          <List>
            <li>Buyers pay upfront. Funds are held in escrow via Stripe until the order is delivered and the buyer either accepts or the 3-day review window elapses.</li>
            <li>Artists set their own base price. Tune4Tune charges a platform fee (currently 15% of the order plus payment-processor fees). The current fee schedule is always visible during checkout.</li>
            <li>Delivery deadlines and revisions are set per artist. If an artist misses a deadline the buyer can cancel for a full refund from within the order page.</li>
          </List>
        </Section>

        <Section id="content-and-ip" title="4. Content and intellectual property">
          <List>
            <li>You keep the copyright in everything you upload (recordings, artwork, mixdowns). By uploading you grant Tune4Tune a worldwide, non-exclusive licence to host, transmit, and display it as needed to run the service.</li>
            <li>When an artist delivers a dubplate to a buyer, the artist grants the buyer a <strong className="text-white">personal, non-commercial licence</strong> to play and share the delivered file. Commercial use (radio play, monetized streaming, syncs) requires a separate written agreement between buyer and artist.</li>
            <li>Do not upload anything that infringes someone else's copyright, defames a person, promotes hate or violence, or contains sexual content involving minors. We remove anything that does and terminate accounts that repeat.</li>
            <li>We honour valid DMCA takedown notices. Send them to <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a>.</li>
          </List>
        </Section>

        <Section id="acceptable-use" title="5. Acceptable use">
          <p>You agree not to:</p>
          <List>
            <li>Impersonate someone else, create fake artist profiles, or misrepresent your identity.</li>
            <li>Attempt to break, probe, or circumvent our security controls.</li>
            <li>Scrape the site, or use bots to place orders or crawl artist profiles.</li>
            <li>Use the service to launder money, evade sanctions, or pay for illegal goods.</li>
            <li>Solicit an artist to move a transaction off-platform to avoid fees — this is the fastest way to get both accounts banned.</li>
          </List>
        </Section>

        <Section id="payments" title="6. Payments, fees, and refunds">
          <List>
            <li>All prices are in US dollars unless stated otherwise. Payment is processed by Stripe.</li>
            <li>Buyers can cancel and receive a full refund <strong className="text-white">before</strong> the artist accepts the order.</li>
            <li>After acceptance, the artist has 3–7 days (set per artist) to deliver. If they don't, the buyer gets a full refund on request.</li>
            <li>After delivery, the buyer has 3 days to accept or open a dispute. If the buyer takes no action, the order auto-completes and funds are released to the artist.</li>
            <li>Disputes are reviewed by Tune4Tune within 5 business days. Our decision is final for platform purposes but does not limit any statutory right you have.</li>
            <li>Chargebacks will be contested on the artist's behalf using the delivery evidence in the order log.</li>
          </List>
        </Section>

        <Section id="disputes" title="7. Disputes between users">
          <p>Tune4Tune is a marketplace. We are not a party to the contract between buyer and artist. We provide the dispute resolution tool inside the order page and will decide close calls in good faith based on the evidence submitted by both sides.</p>
        </Section>

        <Section id="termination" title="8. Termination">
          <p>We may suspend or terminate your account if you materially breach these Terms or engage in fraud. You can close your account at any time from <Link to="/settings?tab=privacy" className="text-lime hover:underline">Settings → Privacy</Link>. Sections 4 (content licence for already-delivered orders), 6 (payment obligations for orders in flight), 9 (disclaimer / liability), and 10 (governing law) survive termination.</p>
        </Section>

        <Section id="disclaimers" title="9. Disclaimers &amp; limitation of liability">
          <p className="uppercase text-xs tracking-wider text-[#A1A1AA]">Read this carefully — the caps are US-standard and there for a reason.</p>
          <List>
            <li>The service is provided <strong className="text-white">"as is"</strong> without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, or non-infringement.</li>
            <li>We do not warrant that a specific artist will accept your order, or that a specific delivery will meet your subjective creative standards. The dispute flow exists for that.</li>
            <li>To the maximum extent permitted by law, Tune4Tune's aggregate liability to you for any claim arising out of the service is limited to the greater of (a) the amount you paid Tune4Tune in the 12 months before the claim arose, or (b) US$100.</li>
            <li>We are not liable for indirect, incidental, consequential, special, or punitive damages, or for lost profits, revenues, or data.</li>
          </List>
        </Section>

        <Section id="governing-law" title="10. Governing law &amp; disputes with Tune4Tune">
          <p>These Terms are governed by the laws of <strong className="text-white">{JURISDICTION}</strong>, without regard to conflict-of-law principles. Any dispute between you and Tune4Tune will be resolved exclusively in the state or federal courts located in Miami-Dade County, Florida, and you consent to their personal jurisdiction. Nothing in this section waives any non-waivable consumer right you have under your local law.</p>
        </Section>

        <Section id="changes" title="11. Changes to these Terms">
          <p>We may update these Terms from time to time. Material changes will be announced by email or an in-app banner at least 14 days before they take effect. Continued use of the service after the effective date means you accept the updated Terms.</p>
        </Section>

        <Section id="contact" title="12. Contact">
          <p>
            {LEGAL_ENTITY}<br />
            {JURISDICTION}<br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a>
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
